import Box from "@mui/material/Box";
import type { SxProps, Theme } from "@mui/material/styles";

import { CheckIcon } from "@/components/marketing/MarketingIcons";
import {
  ACCENT,
  MID,
  NOT_WIDE,
  TINY,
  marketingPalette,
} from "@/components/marketing/tokens";
import type { MatrixAxis, MatrixRow } from "@/content/landing/types";

/**
 * `.resi-table_content`: the ethics page's graded table. The tier column
 * stands on the left, each tier over the list of what it grants, ticked; the
 * description and the example providers run beside it in two columns of
 * their own. Below the wide step the tier column keeps a fixed width and the
 * two columns scroll under it, a shadow marking the edge.
 */

const RULE = marketingPalette.wash;

const CONTENT = {
  display: "flex",
  width: 1,
  border: `1px solid ${RULE}`,
  borderRadius: "16px",
  overflow: "hidden",
  [NOT_WIDE]: { position: "relative" },
  [TINY]: { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
} satisfies SxProps<Theme>;

/** `.resi-table_grid`: the tier column, a third of the row, then a fixed measure the right columns scroll under. */
const TIER_COLUMN = {
  display: "grid",
  gridTemplateColumns: "1fr",
  width: "33.33%",
  [NOT_WIDE]: {
    position: "relative",
    zIndex: 7,
    flex: "none",
    width: "276.8px",
    backgroundColor: "common.white",
  },
  [MID]: { width: "248px" },
  [TINY]: { width: "155.2px" },
} satisfies SxProps<Theme>;

/** `.resi-table-right_wrap`: the two text columns, scrolling below the wide step. */
const RIGHT = {
  display: "flex",
  width: "66.66%",
  [NOT_WIDE]: { width: 1, position: "relative", overflow: "auto" },
} satisfies SxProps<Theme>;

/** `.resi-table_grid.is-100`: each of the right columns, at a fixed measure once the pair scrolls. */
const TEXT_COLUMN = {
  display: "grid",
  gridTemplateColumns: "1fr",
  width: 1,
  [NOT_WIDE]: { flex: "none", width: "300px" },
  [MID]: { width: "240px" },
  [TINY]: { width: "177.6px" },
} satisfies SxProps<Theme>;

/** `.resi-table_overlay`: the shadow the fixed column throws over what scrolls under it. */
const OVERLAY = {
  display: "none",
  [NOT_WIDE]: {
    display: "block",
    position: "absolute",
    top: 0,
    bottom: 0,
    left: 0,
    zIndex: 100,
    width: "276.8px",
    pointerEvents: "none",
    boxShadow: "0 0 30px 6px rgba(13, 11, 43, 0.13)",
  },
  [MID]: { width: "248px" },
  [TINY]: { width: "155.2px" },
} satisfies SxProps<Theme>;

/** `.resi-table_title`: the column heading, small capitals on the grey ground. */
const TITLE = {
  p: "12px 16px",
  borderRight: `1px solid ${RULE}`,
  borderBottom: `1px solid ${RULE}`,
  backgroundColor: marketingPalette.surface,
  color: "rgba(79, 92, 101, 0.8)",
  fontSize: 14.08,
  fontWeight: 500,
  letterSpacing: "1px",
  textTransform: "uppercase",
  [TINY]: { px: "8px", fontSize: 12.16 },
} satisfies SxProps<Theme>;

const TITLE_LAST = { borderRight: 0 } satisfies SxProps<Theme>;

/** `.resi-table_body`: a cell of fixed height, 16 of padding, ruled right and below. */
const CELL = {
  display: "flex",
  gap: "16px",
  height: "127.04px",
  p: "16px",
  borderRight: `1px solid ${RULE}`,
  borderBottom: `1px solid ${RULE}`,
  backgroundColor: "common.white",
  color: marketingPalette.inkMuted,
  fontSize: 14.08,
  lineHeight: "22.08px",
  [MID]: {
    height: "144px",
    color: "rgba(53, 62, 68, 0.8)",
    fontSize: 12.96,
    lineHeight: "20.736px",
  },
  [TINY]: { p: "6.4px 8px" },
} satisfies SxProps<Theme>;

/** `.resi-table_body.is-vertical`: the tier cell, its name beside its list, and over it on a phone. */
const TIER_CELL = {
  [NOT_WIDE]: { gap: "8px" },
  [TINY]: { flexDirection: "column", gap: "6.4px" },
} satisfies SxProps<Theme>;

/**
 * `.resi-table_body_last`: the last column's cell, wider at the sides and with
 * no rule on its right. A class of its own rather than a modifier, so it keeps
 * its 14 on 22 below the card step, where the other cells drop to 13 on 160%.
 */
const CELL_LAST = {
  alignItems: "flex-start",
  gap: "20px",
  px: "24px",
  borderRight: 0,
  [MID]: { fontSize: 14.08, lineHeight: "22.08px" },
  [TINY]: { p: "8px", fontSize: 12.64 },
} satisfies SxProps<Theme>;

/** `.resi-table-body_title` */
const TIER_NAME = {
  width: 1,
  maxWidth: "72.96px",
  color: marketingPalette.ink,
  fontSize: 16,
  lineHeight: "20px",
  fontWeight: 500,
  [MID]: { maxWidth: "60.8px", fontSize: 15.04 },
  [TINY]: { maxWidth: "none" },
} satisfies SxProps<Theme>;

/** `.resi-table-body_list` */
const GRANTS = {
  display: "flex",
  flexDirection: "column",
  gap: "2.08px",
  width: 1,
  [TINY]: { gap: 0 },
} satisfies SxProps<Theme>;

/** `.resi-table-body_item`: a tick and a line; the limited one in grey behind a warning mark. */
const GRANT = {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  fontSize: 14.08,
  lineHeight: "22px",
  [MID]: { color: marketingPalette.inkMuted },
  [TINY]: { fontSize: 13.44 },
} satisfies SxProps<Theme>;

/**
 * `.resi-table-body_item.is-disabled`: a grant the tier has only in part or not
 * at all, faded, and in its grey at every step.
 */
const GRANT_OFF = {
  color: "#83898E",
  opacity: 0.45,
  [MID]: { color: "#83898E" },
} satisfies SxProps<Theme>;

/** `.two_rows_eth`: the limited line closes its leading on a phone. */
const GRANT_LIMITED_LINE = {
  [TINY]: { lineHeight: "17px" },
} satisfies SxProps<Theme>;

/** `.resi-table-body_check` / `.resi-table-body_cross`: 12 square, the tick in the success green. */
const MARK = {
  flex: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "12px",
  height: "12px",
} satisfies SxProps<Theme>;

/** `.resi-table-body_cross.is-red`: the cross, at 60%, before a grant the tier lacks. */
function CrossMark() {
  return (
    <Box
      component="svg"
      viewBox="0 0 12 12"
      aria-hidden
      sx={[MARK, { color: marketingPalette.danger }]}
    >
      <path
        opacity="0.6"
        fillRule="evenodd"
        clipRule="evenodd"
        fill="currentColor"
        d="M0 6C0 2.69063 2.69063 0 6 0C9.30937 0 12 2.69063 12 6C12 9.30937 9.30937 12 6 12C2.69063 12 0 9.30937 0 6ZM4.21213 3.78787C4.09497 3.67071 3.90503 3.67071 3.78787 3.78787C3.67071 3.90503 3.67071 4.09497 3.78787 4.21213L5.57574 6L3.78787 7.78787C3.67071 7.90503 3.67071 8.09497 3.78787 8.21213C3.90503 8.32929 4.09497 8.32929 4.21213 8.21213L6 6.42426L7.78787 8.21213C7.90503 8.32929 8.09497 8.32929 8.21213 8.21213C8.32929 8.09497 8.32929 7.90503 8.21213 7.78787L6.42426 6L8.21213 4.21213C8.32929 4.09497 8.32929 3.90503 8.21213 3.78787C8.09497 3.67071 7.90503 3.67071 7.78787 3.78787L6 5.57574L4.21213 3.78787Z"
      />
    </Box>
  );
}

/** `.resi-table-body_cross.is-yellow`: the warning triangle before a limited grant, 0.8rem square. */
function WarningMark() {
  return (
    <Box
      component="svg"
      viewBox="0 0 512 512"
      aria-hidden
      sx={[MARK, { width: "12.8px", height: "12.8px", color: "#F5B400" }]}
    >
      <path
        fill="currentColor"
        d="M501.362,383.95L320.497,51.474c-29.059-48.921-99.896-48.986-128.994,0L10.647,383.95 c-29.706,49.989,6.259,113.291,64.482,113.291h361.736C495.039,497.241,531.068,433.99,501.362,383.95z M256,437.241 c-16.538,0-30-13.462-30-30c0-16.538,13.462-30,30-30c16.538,0,30,13.462,30,30C286,423.779,272.538,437.241,256,437.241z M286,317.241c0,16.538-13.462,30-30,30c-16.538,0-30-13.462-30-30v-150c0-16.538,13.462-30,30-30c16.538,0,30,13.462,30,30 V317.241z"
      />
    </Box>
  );
}

/** A column heading, whichever shape the axis takes. */
const labelOf = (axis: MatrixAxis | undefined) =>
  axis === undefined ? "" : typeof axis === "string" ? axis : axis.label;

type Grant = { text: string; state: "granted" | "limited" | "lacking" };

/**
 * The grants a tier's first cell lists, one per dot-separated part: the
 * footnoted one is the limited one, and the cell names the ones it lacks.
 */
function grantsOf(cell: MatrixRow["cells"][number] | undefined): Array<Grant> {
  const lacks = new Set(cell?.lacks ?? []);
  return (cell?.value ?? "").split(" · ").map((text) => ({
    text,
    state: text.startsWith("*")
      ? "limited"
      : lacks.has(text)
        ? "lacking"
        : "granted",
  }));
}

/** The mark a grant opens with, by what the tier has of it. */
function GrantMark({ state }: { state: Grant["state"] }) {
  if (state === "limited") {
    return <WarningMark />;
  }
  if (state === "lacking") {
    return <CrossMark />;
  }
  return (
    <Box sx={[MARK, { color: ACCENT }]}>
      <CheckIcon size={12} />
    </Box>
  );
}

export default function MatrixGraded({
  columns,
  rows,
}: {
  columns: ReadonlyArray<MatrixAxis>;
  rows: ReadonlyArray<MatrixRow>;
}) {
  const [tierTitle, ...textTitles] = columns;
  return (
    <Box sx={CONTENT}>
      <Box aria-hidden sx={OVERLAY} />
      <Box sx={TIER_COLUMN}>
        <Box sx={TITLE}>{labelOf(tierTitle)}</Box>
        {rows.map((row) => (
          <Box key={row.label} sx={[CELL, TIER_CELL]}>
            <Box sx={TIER_NAME}>{row.label}</Box>
            <Box sx={GRANTS}>
              {grantsOf(row.cells[0]).map((grant, index) => (
                <Box
                  // The CMS can repeat a grant's text; the list is static.
                  key={index}
                  sx={[GRANT, grant.state === "granted" ? null : GRANT_OFF]}
                >
                  <GrantMark state={grant.state} />
                  <Box
                    sx={grant.state === "limited" ? GRANT_LIMITED_LINE : null}
                  >
                    {grant.text}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
      <Box sx={RIGHT}>
        {textTitles.map((column, at) => {
          const last = at === textTitles.length - 1;
          return (
            // Two columns can carry one label; the list is static.
            <Box key={at} sx={TEXT_COLUMN}>
              <Box sx={[TITLE, last ? TITLE_LAST : null]}>
                {labelOf(column)}
              </Box>
              {rows.map((row) => (
                <Box key={row.label} sx={[CELL, last ? CELL_LAST : null]}>
                  <Box>{row.cells[at + 1]?.value}</Box>
                </Box>
              ))}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
